import * as errors from "../errors.js";
import { state } from "../state.js";

// Internal seed for the PRNG sequence.
let currentSeed = 1;

// Linear Congruential Generator (LCG) for pseudo-random numbers.
const lcg = (seed) => {
    const a = 1664525; // Multiplier
    const c = 1013904223; // Increment
    const m = 2 ** 32; // Modulus
    return (a * seed + c) % m;
};

// Generate a random number between 0.0 (inclusive) and 1.0 (exclusive) using the LCG.
const randomFromSeed = (seed) => {
    const nextSeed = lcg(seed);
    currentSeed = nextSeed; // Update the global seed
    return nextSeed / (2 ** 32); // Normalize to [0, 1)
};

/*
    The function RND generates pseudo random[1] floating point numbers in the range of 0.0 (inclusive) to 1.0 (exclusive). The argument <number> can be positive, negative or zero.
    By using RND(<positive number>) gives a different random number each time from a predetermined sequence (the sequence number is stored internally).
    Using RND(<negative number>) jumps to a point in the sequence determined by the particular negative number used. Repeatedly calling RND with the same negative number results in 
    the same result; typical use is to call RND(<negative number>) once and then repeatedly call RND(<positive number>).
    With RND(0) the C64 generates the random number from the internal timer[2] and real-time clock[3], though the possible values are limited as long the RTC isn't running to only 
    16 bit for the 32 bit wide floating point mantissa.
    When using a invalid numeric value, the BASIC error ?TYPE MISMATCH ERROR appears. When the numeric argument is absent, it appears as ?SYNTAX ERROR. 
*/
export const rnd = async (args) => {
    // If no args are given, throw a syntax error.
    if (args.length === 0) {
        throw new errors.SyntaxError();
    }

    // Check if the args is a number.
    if (isNaN(args)) {
        throw new errors.TypeMismatchError();
    }

    // Check for overflow.
    if (Math.abs(args) > state.maxValue) {
        throw new errors.OverflowError();
    }

    const num = Number(args);

    if (num > 0) {
        // Positive number: Generate a random number from the current sequence.
        return randomFromSeed(currentSeed);
    } else if (num < 0) {
        // Negative number: Use the absolute value of the number as the seed.
        currentSeed = Math.abs(num);
        return randomFromSeed(currentSeed);
    } else {
        // Zero: Generate a random number based on the current time.
        const timeSeed = Date.now() % (2 ** 32); // Use the current time as a seed
        return randomFromSeed(timeSeed);
    }
};